/** @fileoverview Renders tool calls as expandable activity entries. */

import {
  BookOpenCheck,
  BookX,
  CheckCircle2,
  ChevronDown,
  LoaderCircle,
  XCircle,
} from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { ScrollArea, ScrollAreaContent, ScrollAreaViewport, ScrollBar } from '../ui/scroll-area';
import type {
  ChatActivityProps,
  ChatSkillActivityProps,
  SkillActivityEntry,
  ToolActivityEntry,
  ToolCallRowProps,
} from './types';

const statusOf = (status: ToolActivityEntry['status']) =>
  status === 'completed'
    ? { label: 'Completed', Icon: CheckCircle2 }
    : status === 'failed'
    ? { label: 'Failed', Icon: XCircle }
    : { label: 'Running', Icon: LoaderCircle };

const displayToolName = (entry: ToolActivityEntry) => entry.toolName;

const toolTitle = (entry: ToolActivityEntry) =>
  `${entry.status === 'running' ? 'Running' : 'Ran'} ${displayToolName(entry)}`;

const formatValue = (value: unknown) =>
  typeof value === 'string' ? value : JSON.stringify(value, null, 2) ?? String(value);

const withoutIntent = (value: unknown) =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? Object.fromEntries(Object.entries(value).filter(([key]) => key !== 'intent'))
    : value;

const ToolDetail = ({ label, value }: { label: string; value: unknown }) => (
  <div className="min-w-0">
    <div className="mb-1 text-xs font-medium text-app-text-muted">{label}</div>
    <pre className="m-0 w-max min-w-full whitespace-pre p-2 text-xs text-app-text">
      <code>{formatValue(value)}</code>
    </pre>
  </div>
);

const ToolCallRow = ({ entry }: ToolCallRowProps) => {
  const { label, Icon } = statusOf(entry.status);
  const response = entry.errorText
    ? { error: entry.errorText }
    : entry.output === undefined
    ? { message: 'Tool result is not available.' }
    : entry.output;
  const title = toolTitle(entry);
  const activityTitle = entry.activityLabel || title;
  const ariaLabel = `${
    entry.intent ? `${entry.intent}. ` : ''
  }${activityTitle}, ${label.toLowerCase()}`;

  return (
    <Collapsible className="w-full min-w-0 max-w-full" key={entry.key}>
      <CollapsibleTrigger
        aria-label={ariaLabel}
        className="group flex min-w-0 w-full items-center gap-2 py-1 text-left text-sm text-app-text-muted hover:text-app-text-secondary"
        title={label}
      >
        <Icon
          aria-hidden="true"
          className={`h-3.5 w-3.5 shrink-0 ${entry.status === 'running' ? 'animate-spin' : ''}`}
        />
        <span className="min-w-0 flex-1 truncate">
          {entry.intent && (
            <span className="mr-1 font-normal text-app-text-muted">{entry.intent}</span>
          )}
          <span className="font-medium text-app-text-secondary">{activityTitle}</span>
        </span>
        <ChevronDown className="ml-auto h-3.5 w-3.5 shrink-0 transition-transform group-data-[state=open]:rotate-180" />
      </CollapsibleTrigger>
      <CollapsibleContent className="w-full min-w-0 max-w-full pl-5 pt-1">
        <ScrollArea className="max-h-56 w-full min-w-0 max-w-full rounded-md bg-app-surface-card">
          <ScrollAreaViewport className="h-auto! max-h-56 min-w-0 max-w-full overscroll-contain">
            <ScrollAreaContent className="w-max min-w-full">
              <div className="space-y-3 p-2">
                {entry.input !== undefined && (
                  <ToolDetail label="Request" value={withoutIntent(entry.input)} />
                )}
                <ToolDetail label="Response" value={response} />
              </div>
            </ScrollAreaContent>
          </ScrollAreaViewport>
          <ScrollBar />
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
      </CollapsibleContent>
    </Collapsible>
  );
};

/** Each contiguous tool batch has its own row at its original position in the stream. */
export default function ChatActivity({ entries }: ChatActivityProps) {
  if (!entries.length) return null;

  const activeTool = [...entries].reverse().find((entry) => entry.status === 'running');
  const title = activeTool
    ? toolTitle(activeTool)
    : `${entries.length} tool ${entries.length === 1 ? 'call' : 'calls'}`;
  const GroupIcon = activeTool ? LoaderCircle : CheckCircle2;

  return (
    <Collapsible className="w-full min-w-0 max-w-full rounded-md border border-app-border/70">
      <CollapsibleTrigger className="group flex min-w-0 w-full items-center gap-2 px-2 py-1.5 text-left text-sm text-app-text-muted hover:text-app-text-secondary">
        <GroupIcon
          aria-hidden="true"
          className={`h-3.5 w-3.5 shrink-0 ${activeTool ? 'animate-spin' : ''}`}
        />
        <span className="min-w-0 truncate">{title}</span>
        <ChevronDown className="ml-auto h-3.5 w-3.5 shrink-0 transition-transform group-data-[state=open]:rotate-180" />
      </CollapsibleTrigger>
      <CollapsibleContent className="w-full min-w-0 max-w-full px-2 pb-2">
        <div className="ml-1 min-w-0 max-w-full space-y-1 border-l border-app-border pl-3">
          {entries.map((entry) => (
            <ToolCallRow entry={entry} key={entry.key} />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

const skillText = (entry: SkillActivityEntry) => {
  const name = entry.skillName ? ` ${entry.skillName} Skill` : ' a skill';
  return entry.phase === 'completed'
    ? `Used${name}`
    : entry.phase === 'failed'
    ? `Failed to read${name}`
    : `Reading ${entry.skillName ?? 'a skill'}`;
};

/** Skill reads stay as lightweight timeline rows rather than tool-call disclosures. */
export function ChatSkillActivity({ entry }: ChatSkillActivityProps) {
  const { phase } = entry;
  const { Icon, label } =
    phase === 'completed'
      ? { Icon: BookOpenCheck, label: 'Completed' }
      : phase === 'failed'
      ? { Icon: BookX, label: 'Failed' }
      : { Icon: LoaderCircle, label: 'In progress' };
  const text = skillText(entry);

  return (
    <div
      aria-label={`${text}, ${label.toLowerCase()}`}
      className="flex min-w-0 items-center gap-2 py-0.5 text-sm text-app-text-muted"
      title={label}
    >
      <Icon
        aria-hidden="true"
        className={`h-3.5 w-3.5 shrink-0 ${phase === 'started' ? 'animate-spin' : ''}`}
      />
      <span className="min-w-0 truncate">{text}</span>
    </div>
  );
}
